import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./Portfolio.css";
import publicOrigin from "./publicOrigin";
import { clearAuthStorage } from "./auth/logout";

const MAX_IMAGES = 40;

function Portfolio() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  // Event types
  const [eventTypes, setEventTypes] = useState([]);

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Portfolio events
  const [portfolioEvents, setPortfolioEvents] = useState([]);
  const [shareToken, setShareToken] = useState(null);

  // Expanded event for image management
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [eventImages, setEventImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedEventId, setCopiedEventId] = useState(null);

  const fetchEventTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/event-types`);
      const data = await res.json();
      setEventTypes(data.eventTypes || []);
    } catch { /* ignore */ }
  }, []);

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/${userId}`);
      const data = await res.json();
      setPortfolioEvents(data.portfolioEvents || []);
      setShareToken(data.shareToken || null);
    } catch { /* ignore */ }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchEventTypes();
    fetchPortfolio();
  }, [fetchEventTypes, fetchPortfolio]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 4000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  // Filtered event types for dropdown
  const filtered = eventTypes.filter((et) =>
    et.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const exactMatch = eventTypes.some((et) => et.name.toLowerCase() === searchTerm.trim().toLowerCase());

  // Select existing event type → add to portfolio
  const handleSelectType = async (eventType) => {
    setDropdownOpen(false);
    setSearchTerm("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, eventTypeId: eventType.id }),
      });
      if (res.status === 409) {
        setError(`"${eventType.name}" is already in your portfolio`);
        return;
      }
      setSuccess(`"${eventType.name}" added to portfolio!`);
      fetchPortfolio();
    } catch {
      setError("Failed to add to portfolio");
    }
  };

  // Create new event type → then add to portfolio
  const handleCreateAndAdd = async () => {
    if (!searchTerm.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/event-types`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: searchTerm.trim(), userId }),
      });
      const data = await res.json();
      fetchEventTypes();
      // Now add to portfolio
      const addRes = await fetch(`${API_BASE_URL}/api/portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, eventTypeId: data.eventType.id }),
      });
      if (addRes.status === 409) {
        setError(`"${data.eventType.name}" is already in your portfolio`);
      } else {
        setSuccess(`"${data.eventType.name}" created & added!`);
        fetchPortfolio();
      }
      setSearchTerm("");
      setDropdownOpen(false);
    } catch {
      setError("Failed to create event type");
    }
  };

  // Delete portfolio event
  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Delete this event and all its images?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // The server rejects this now — a signed-out session, another studio's
        // portfolio, or a team member below 'editor'. "Failed to delete" tells
        // a view-only member nothing; its own message says why.
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || "Failed to delete");
      }
      setSuccess("Deleted");
      if (expandedEvent === id) { setExpandedEvent(null); setEventImages([]); }
      fetchPortfolio();
    } catch (err) {
      setError(err.message || "Failed to delete");
    }
  };

  // Expand event → load images
  const handleExpand = async (eventId) => {
    if (expandedEvent === eventId) { setExpandedEvent(null); setEventImages([]); return; }
    setExpandedEvent(eventId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/${eventId}/images`);
      const data = await res.json();
      setEventImages(data.images || []);
    } catch {
      setEventImages([]);
    }
  };

  // Upload images
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !expandedEvent) return;
    const currentCount = eventImages.length;
    const remaining = MAX_IMAGES - currentCount;
    if (remaining <= 0) { setError(`Maximum ${MAX_IMAGES} images reached`); return; }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      setError(`Only uploading ${remaining} of ${files.length} images (max ${MAX_IMAGES})`);
    }
    setUploading(true);
    let uploaded = 0;
    for (const file of toUpload) {
      setUploadProgress(`Uploading ${++uploaded}/${toUpload.length}...`);
      try {
        const base64 = await fileToBase64(file);
        const res = await fetch(`${API_BASE_URL}/api/portfolio/${expandedEvent}/images`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageData: base64 }),
        });
        if (res.ok) {
          const data = await res.json();
          setEventImages((prev) => [...prev, data.image]);
        }
      } catch { /* ignore single failure */ }
    }
    setUploading(false);
    setUploadProgress("");
    setSuccess(`${uploaded} image(s) uploaded`);
    fetchPortfolio();
    e.target.value = "";
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Delete image
  const handleDeleteImage = async (imageId) => {
    try {
      await fetch(`${API_BASE_URL}/api/portfolio/images/${imageId}`, { method: "DELETE" });
      setEventImages((prev) => prev.filter((i) => i.id !== imageId));
      fetchPortfolio();
    } catch {
      setError("Failed to delete image");
    }
  };

  // Generate share link
  const handleShare = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      setShareToken(data.token);
      setSuccess("Share link generated!");
    } catch {
      setError("Failed to generate link");
    }
  };

  const copyLink = () => {
    const url = `${publicOrigin()}/portfolio/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Copy per-event share link (generates token if needed)
  const copyEventLink = async (eventId) => {
    let tk = shareToken;
    if (!tk) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/portfolio/share`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        });
        const data = await res.json();
        tk = data.token;
        setShareToken(tk);
      } catch {
        setError("Failed to generate share link");
        return;
      }
    }
    const url = `${publicOrigin()}/portfolio/${tk}?event=${eventId}`;
    navigator.clipboard.writeText(url);
    setCopiedEventId(eventId);
    setTimeout(() => setCopiedEventId(null), 2000);
  };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  return (
    <div className="pf-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`pf-main ${sidebarOpen ? "" : "sidebar-closed"}`}>
        <header className="pf-header">
          <div className="pf-header-left">
            <button className="pf-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1>📁 Portfolio</h1>
          </div>
          <button className="pf-logout-btn" onClick={handleLogout}>Logout</button>
        </header>

        {error && <div className="pf-alert pf-alert-error">{error}</div>}
        {success && <div className="pf-alert pf-alert-success">{success}</div>}

        <div className="pf-content">
          {/* Add Event + Share Row */}
          <div className="pf-top-bar">
            <div className="pf-add-section" ref={dropdownRef}>
              <div className="pf-search-dropdown">
                <input
                  type="text"
                  className="pf-search-input"
                  placeholder="Search or add event type..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
                  onFocus={() => setDropdownOpen(true)}
                />
                {dropdownOpen && (
                  <div className="pf-dropdown-list">
                    {filtered.length > 0 ? (
                      filtered.map((et) => (
                        <div key={et.id} className="pf-dropdown-item" onClick={() => handleSelectType(et)}>
                          {et.name}
                        </div>
                      ))
                    ) : (
                      <div className="pf-dropdown-empty">No matching event types</div>
                    )}
                    {searchTerm.trim() && !exactMatch && (
                      <div className="pf-dropdown-item pf-dropdown-add" onClick={handleCreateAndAdd}>
                        + Create &amp; add "<strong>{searchTerm.trim()}</strong>"
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="pf-share-section">
              {shareToken ? (
                <div className="pf-share-link-box">
                  <input type="text" readOnly value={`${publicOrigin()}/portfolio/${shareToken}`} />
                  <button onClick={copyLink}>{copied ? "✓ Copied!" : "📋 Copy"}</button>
                </div>
              ) : (
                <button className="pf-generate-btn" onClick={handleShare}>🔗 Share Portfolio</button>
              )}
            </div>
          </div>

          {/* Portfolio Events Table */}
          <div className="pf-card">
            <h2>Your Portfolio</h2>
            {loading ? (
              <CameraLoader text="Loading portfolio..." />
            ) : portfolioEvents.length === 0 ? (
              <p className="pf-empty">No portfolio events yet. Search &amp; add one above!</p>
            ) : (
              <div className="pf-table-wrap">
                <table className="pf-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event Type</th>
                      <th>Images</th>
                      <th>Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioEvents.map((pe, idx) => (
                      <React.Fragment key={pe.id}>
                        <tr className={expandedEvent === pe.id ? "pf-row-expanded" : ""}>
                          <td>{idx + 1}</td>
                          <td><strong>{pe.event_type_name}</strong></td>
                          <td>
                            <span className="pf-badge">{pe.image_count}/{MAX_IMAGES}</span>
                          </td>
                          <td>{new Date(pe.created_at).toLocaleDateString()}</td>
                          <td className="pf-actions">
                            <button className="pf-btn-expand" onClick={() => handleExpand(pe.id)}>
                              {expandedEvent === pe.id ? "▲ Close" : "▼ Images"}
                            </button>
                            <button className="pf-btn-share" onClick={() => copyEventLink(pe.id)} title="Copy share link for this event">
                              {copiedEventId === pe.id ? "✓" : "🔗"}
                            </button>
                            <button className="pf-btn-delete" onClick={() => handleDeleteEvent(pe.id)}>🗑</button>
                          </td>
                        </tr>
                        {expandedEvent === pe.id && (
                          <tr className="pf-expanded-row">
                            <td colSpan={5}>
                              <div className="pf-image-panel">
                                <div className="pf-image-panel-header">
                                  <span>📸 {pe.event_type_name} ({eventImages.length}/{MAX_IMAGES})</span>
                                  <label className="pf-upload-btn">
                                    {uploading ? uploadProgress : "⬆ Upload"}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={handleFileUpload}
                                      disabled={uploading || eventImages.length >= MAX_IMAGES}
                                      hidden
                                    />
                                  </label>
                                </div>
                                {eventImages.length === 0 ? (
                                  <p className="pf-no-images">No images yet. Upload some!</p>
                                ) : (
                                  <div className="pf-image-grid">
                                    {eventImages.map((img) => (
                                      <div key={img.id} className="pf-image-item">
                                        <img src={img.thumbnail_url || img.image_url} alt="" loading="lazy" />
                                        <button className="pf-img-delete" onClick={() => handleDeleteImage(img.id)}>✕</button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
